/**
 * AgentReferrals — Agent Hub Referrals tab
 *
 * Shows:
 *  - Referral link + share buttons + analytics
 *  - Referred agents list with commission due + threshold status
 *  - Commission summary (total earned / held / payable / paid)
 * F-138
 */

import { useState, useEffect, useCallback } from 'react';
import { getAPIBase, getAuthHeaders } from '../lib/api-fetch';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import {
  Copy, Share2, Users, TrendingUp, Clock, CheckCircle2,
  DollarSign, Loader2, RefreshCw, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

interface ReferralLink {
  referralCode: string;
  url: string;
  totalClicks: number;
  totalSignups: number;
  totalConverted: number;
}

interface Referral {
  referralId: string;
  referredAgentId: string;
  referredAgentName: string | null;
  email?: string | null;
  phone?: string | null;
  currentAgency?: string | null;
  referralCode?: string | null;
  notes?: string | null;
  monthlyDealVolume?: number | null;
  commissionSplit?: string | null;
  status: string;
  onboardingDate: string | null;
  submittedAt?: string | null;
  commissionDue: number;
  source?: 'prospect' | 'referral';
}

interface CommissionSummary {
  totalEarned: number;
  held: number;
  payable: number;
  paid: number;
}

interface ThresholdStatus {
  threshold: number;
  currentDeals: number;
  met: boolean;
  message: string;
}

function formatZAR(amount: number): string {
  return `R ${amount.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function AgentReferrals() {
  const [linkData, setLinkData] = useState<ReferralLink | null>(null);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [summary, setSummary] = useState<CommissionSummary | null>(null);
  const [thresholdStatus, setThresholdStatus] = useState<ThresholdStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [linkRes, referralsRes] = await Promise.all([
        fetch(`${getAPIBase()}/agents/referral-link`, { headers: getAuthHeaders() }),
        fetch(`${getAPIBase()}/agents/my-referrals`, { headers: getAuthHeaders() }),
      ]);
      const [linkJson, referralsJson] = await Promise.all([linkRes.json(), referralsRes.json()]);

      if (linkJson.success) setLinkData(linkJson.data);
      if (referralsJson.success) {
        setReferrals(referralsJson.data.referrals || []);
        setSummary(referralsJson.data.commissionSummary);
        setThresholdStatus(referralsJson.data.thresholdStatus);
      }
    } catch {
      // silent fail
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copyLink = () => {
    if (!linkData?.url) return;
    navigator.clipboard.writeText(linkData.url).then(() => toast.success('Referral link copied!'));
  };

  const shareWhatsApp = () => {
    if (!linkData?.url) return;
    const text = encodeURIComponent(`Join me at Something Property! Use my referral link: ${linkData.url}`);
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 dark" data-testid="agent-referrals-tab">

      {/* ── Referral Link ── */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground">Your Referral Link</h3>
            <Button variant="ghost" size="icon" onClick={load} data-testid="referral-refresh">
              <RefreshCw className="w-4 h-4 text-muted-foreground" />
            </Button>
          </div>

          {linkData ? (
            <>
              <div className="flex gap-2 mb-4">
                <div className="flex-1 bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground truncate font-mono">
                  {linkData.url}
                </div>
                <Button variant="outline" size="icon" onClick={copyLink} data-testid="referral-copy">
                  <Copy className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={shareWhatsApp} data-testid="referral-share-wa">
                  <Share2 className="w-4 h-4" />
                </Button>
              </div>

              {/* Analytics */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Clicks', value: linkData.totalClicks, icon: TrendingUp },
                  { label: 'Signups', value: linkData.totalSignups, icon: Users },
                  { label: 'Active Agents', value: linkData.totalConverted, icon: CheckCircle2 },
                ].map(({ label, value, icon: Icon }) => (
                  <div key={label} className="text-center p-3 bg-muted rounded-lg">
                    <Icon className="w-4 h-4 text-muted-foreground mx-auto mb-1" />
                    <p className="text-lg font-bold text-foreground">{value}</p>
                    <p className="text-xs text-muted-foreground">{label}</p>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Could not load referral link. Please refresh.</p>
          )}
        </CardContent>
      </Card>

      {/* ── Commission Summary ── */}
      {summary && (
        <Card>
          <CardContent className="pt-5">
            <h3 className="font-semibold text-foreground mb-4">Commission Summary</h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Total Earned', value: summary.totalEarned, color: 'text-foreground' },
                { label: 'Currently Held', value: summary.held, color: 'text-amber-400' },
                { label: 'Payable', value: summary.payable, color: 'text-emerald-400' },
                { label: 'Paid Out', value: summary.paid, color: 'text-primary' },
              ].map(({ label, value, color }) => (
                <div key={label} className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
                  <p className={`font-bold ${color}`}>{formatZAR(value)}</p>
                </div>
              ))}
            </div>

            {/* Threshold indicator */}
            {thresholdStatus && !thresholdStatus.met && (
              <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-amber-400" />
                  <span className="text-sm font-medium text-amber-400">Threshold pending</span>
                </div>
                <p className="text-xs text-muted-foreground">{thresholdStatus.message}</p>
                <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-400 rounded-full transition-all"
                    style={{ width: `${Math.min(100, (thresholdStatus.currentDeals / thresholdStatus.threshold) * 100)}%` }}
                  />
                </div>
              </div>
            )}
            {thresholdStatus?.met && (
              <div className="mt-3 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span className="text-sm text-emerald-400 font-medium">Threshold met — commissions are payable</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Referred People ── */}
      <Card>
        <CardContent className="pt-5">
          <h3 className="font-semibold text-foreground mb-4">
            Referred People <span className="text-muted-foreground font-normal text-sm">({referrals.length})</span>
          </h3>

          {referrals.length === 0 ? (
            <div className="text-center py-10">
              <Users className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-30" />
              <p className="text-sm font-medium text-muted-foreground">No referrals yet</p>
              <p className="text-xs text-muted-foreground mt-1">Share your referral link to start earning</p>
            </div>
          ) : (
            <div className="space-y-2">
              {referrals.map(r => {
                const statusConfig: Record<string, { label: string; className: string }> = {
                  pending_review: { label: 'Pending Review', className: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
                  approved:       { label: 'Approved',       className: 'bg-primary/10 text-primary border-primary/20' },
                  rejected:       { label: 'Rejected',       className: 'bg-destructive/10 text-destructive border-destructive/20' },
                  active:         { label: 'Active Agent',   className: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
                  inactive:       { label: 'Inactive',       className: 'bg-muted text-muted-foreground border-border' },
                };
                const st = statusConfig[r.status] ?? { label: r.status, className: 'bg-muted text-muted-foreground border-border' };
                const initials = (r.referredAgentName || '?').split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase();
                const dateLabel = r.onboardingDate
                  ? `Joined ${new Date(r.onboardingDate).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}`
                  : r.submittedAt
                  ? `Applied ${new Date(r.submittedAt).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}`
                  : null;

                return (
                  <div
                    key={r.referralId}
                    className="flex items-start justify-between p-3 rounded-lg border border-border bg-muted/30 hover:bg-muted/50 transition-colors"
                    data-testid={`referral-${r.referralId}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-primary font-semibold text-sm shrink-0 mt-0.5">
                        {initials}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-medium text-foreground">
                            {r.referredAgentName || 'Unknown'}
                          </p>
                          {r.referralId?.startsWith('REF-') && (
                            <span className="text-xs font-mono text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-0.5 rounded">
                              {r.referralId}
                            </span>
                          )}
                          {r.referralCode && (
                            <span className="text-xs font-mono text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                              via {r.referralCode}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                          {r.email && <p className="text-xs text-muted-foreground">{r.email}</p>}
                          {r.phone && <p className="text-xs text-muted-foreground">{r.phone}</p>}
                          {r.currentAgency && <p className="text-xs text-muted-foreground">{r.currentAgency}</p>}
                          {r.commissionSplit && <p className="text-xs text-muted-foreground">Split: {r.commissionSplit}</p>}
                          {r.monthlyDealVolume != null && <p className="text-xs text-muted-foreground">{r.monthlyDealVolume} deals/mo</p>}
                        </div>
                        {r.notes && (
                          <p className="text-xs text-muted-foreground mt-1 italic line-clamp-2">"{r.notes}"</p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className={`text-xs border ${st.className}`}>
                            {st.label}
                          </Badge>
                          {dateLabel && (
                            <span className="text-xs text-muted-foreground">{dateLabel}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    {r.commissionDue > 0 && (
                      <div className="text-right shrink-0 ml-3">
                        <p className="text-sm font-semibold text-foreground">{formatZAR(r.commissionDue)}</p>
                        <p className="text-xs text-muted-foreground">commission due</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
